import { ArrowLeft, CheckCircle, Loader, Send, XCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../../components/layout/MainLayout'
import './ApiKeyTest.css'

import { API_URL } from '../../config/api'
interface ApiKey {
  name: string
  provider: string
  status: 'online' | 'offline' | 'testing' | 'unknown'
  masked: string
  lastTest?: string
}

export default function ApiKeyTest() {
  const navigate = useNavigate()
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [testMessage, setTestMessage] = useState('Hello, test message')
  const [selectedKey, setSelectedKey] = useState<string>('')
  const [response, setResponse] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const loadKeys = useCallback(async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Failed to load keys')
      }
      const data = await res.json() as { keys: ApiKey[] }
      setKeys(data.keys || [])
    } catch (err) {
      console.error('Failed to load keys:', err)
    }
  }, [])

  useEffect(() => {
    void loadKeys()
  }, [loadKeys])

  const testKey = async (keyName: string) => {
    setLoading(true)
    setResponse('')
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/api-keys/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ key_name: keyName, message: testMessage })
      })
      const data = await res.json() as { success: boolean; response?: string; error?: string }
      setResponse(data.response || data.error || 'No response')

      // Update key status
      setKeys(prev => prev.map(k =>
        k.name === keyName
          ? { ...k, status: data.success ? 'online' : 'offline', lastTest: new Date().toISOString() }
          : k
      ))
    } catch (err) {
      setResponse('Error: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  const testAllKeys = async () => {
    for (const key of keys) {
      await testKey(key.name)
      await new Promise(resolve => setTimeout(resolve, 1000))
    }
  }

  const handleTestClick = () => {
    if (selectedKey) {
      void testKey(selectedKey)
    }
  }

  const handleTestAllClick = () => {
    void testAllKeys()
  }

  return (
    <MainLayout>
      <div className="api-key-test-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              className="btn-secondary"
              onClick={() => navigate('/admin')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <ArrowLeft size={18} />
              Back to Admin
            </button>
            <h1>API Key Test</h1>
          </div>
          <button className="btn-primary" onClick={handleTestAllClick} disabled={loading}>
            Test All Keys
          </button>
        </div>

      <div className="test-layout">
        <div className="keys-panel">
          <h2>API Keys ({keys.length})</h2>
          <div className="keys-list">
            {keys.map((key, idx) => (
              <div
                key={idx}
                className={`key-item ${selectedKey === key.name ? 'active' : ''} ${key.status}`}
                onClick={() => setSelectedKey(key.name)}
              >
                <div className="key-info">
                  <h3>{key.name}</h3>
                  <span className="key-provider">{key.provider}</span>
                  <span className="key-masked">{key.masked}</span>
                  {key.lastTest && (
                    <span className="last-test">
                      Last: {new Date(key.lastTest).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div className="key-status">
                  {key.status === 'online' && <CheckCircle size={20} color="#22c55e" />}
                  {key.status === 'offline' && <XCircle size={20} color="#ef4444" />}
                  {key.status === 'testing' && <Loader size={20} className="spin" />}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="test-panel">
          <h2>Test Chat</h2>

          <div className="test-input">
            <input
              type="text"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Enter test message..."
            />
            <button
              className="btn-primary"
              onClick={handleTestClick}
              disabled={!selectedKey || loading}
            >
              <Send size={18} />
              Test
            </button>
          </div>

          {response && (
            <div className="test-response">
              <h3>Response:</h3>
              <pre>{response}</pre>
            </div>
          )}

          {loading && (
            <div className="loading-indicator">
              <Loader size={24} className="spin" />
              Testing...
            </div>
          )}
        </div>
      </div>
      </div>
    </MainLayout>
  )
}
